/**
 * Lightweight GA4 custom-event helper.
 *
 * The gtag loader itself is injected at runtime by `useGoogleAnalytics`
 * (driven by the admin-configured measurement ID). This module is the single
 * place the app fires *custom* events through that same tag — there is no
 * second analytics pipeline.
 *
 * `trackEvent` is a safe no-op whenever `window.gtag` is missing (dev/preview,
 * before the tag loads, or when no GA ID is configured), so call sites can fire
 * events unconditionally without breaking anything.
 *
 * Only non-PII parameters should ever be passed (ids, amounts, method names).
 * Never send emails, names, tokens, or raw account details — consistent with
 * GA's "Redact data" requirement.
 */

/** Canonical custom-event names. Use these constants instead of raw strings. */
export const AnalyticsEvent = {
  SignUp: "sign_up",
  ShowCreated: "show_created",
  ShowImportStarted: "show_import_started",
  EpisodeUploaded: "episode_uploaded",
  BookingStarted: "booking_started",
  BookingCompleted: "booking_completed",
  PayoutMethodAdded: "payout_method_added",
  PayoutRequested: "payout_requested",
  PaymentPlaceholderClicked: "payment_placeholder_clicked",
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

type AnalyticsParams = Record<string, string | number | boolean | undefined>;

export function trackEvent(
  name: AnalyticsEventName,
  params?: AnalyticsParams,
): void {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag("event", name, params ?? {});
}
